import type { EmailStatus } from '../types';
import { Send, CheckCheck, Eye, MousePointer, AlertTriangle, XCircle, Clock } from 'lucide-react';

interface EmailStatusBadgeProps {
  status: EmailStatus;
  size?: 'sm' | 'md';
  showIcon?: boolean;
}

const statusConfig: Record<EmailStatus, { label: string; className: string; icon: typeof Send }> = {
  draft: {
    label: 'Draft',
    className: 'bg-[var(--asi-bg-tertiary)] text-[var(--asi-text-secondary)]',
    icon: Clock,
  },
  queued: {
    label: 'Queued',
    className: 'bg-[#FFF7ED] text-[#C2410C]',
    icon: Clock,
  },
  sent: {
    label: 'Sent',
    className: 'bg-[#EFF6FF] text-[#2563EB]',
    icon: Send,
  },
  delivered: {
    label: 'Delivered',
    className: 'bg-[#ECFDF5] text-[#059669]',
    icon: CheckCheck,
  },
  opened: {
    label: 'Opened',
    className: 'bg-[#F3E8FF] text-[#7C3AED]',
    icon: Eye,
  },
  clicked: {
    label: 'Clicked',
    className: 'bg-[#EDE9FE] text-[#6D28D9]',
    icon: MousePointer,
  },
  bounced: {
    label: 'Bounced',
    className: 'bg-[#FEF2F2] text-[#DC2626]',
    icon: AlertTriangle,
  },
  failed: {
    label: 'Failed',
    className: 'bg-[#FEF2F2] text-[#DC2626]',
    icon: XCircle,
  },
};

const sizeStyles = {
  sm: 'px-2 py-0.5 text-[10px] gap-1',
  md: 'px-2.5 py-1 text-xs gap-1.5',
};

export function EmailStatusBadge({ status, size = 'md', showIcon = true }: EmailStatusBadgeProps) {
  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium tracking-tight ${config.className} ${sizeStyles[size]}`}
    >
      {showIcon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />}
      {config.label}
    </span>
  );
}

// Helper to get status progression level (for timeline display)
export function getStatusLevel(status: EmailStatus): number {
  const levels: Record<EmailStatus, number> = {
    draft: 0,
    queued: 1,
    sent: 2,
    delivered: 3,
    opened: 4,
    clicked: 5,
    bounced: -1,
    failed: -1,
  };
  return levels[status] ?? 0;
}
